/**
 * Ride Westside - Event filtering and collapsible sections
 */

import { parseISO, isBefore, isAfter, addDays, startOfDay } from "date-fns";

interface EventElement {
  element: HTMLElement;
  date: Date;
}

interface FilterState {
  start: string;
  end: string;
  tag: string;
}

const DAYS_THRESHOLD = 90;
const FILTER_STORAGE_KEY = "ridewestside:filters";
const SETTINGS_STORAGE_KEY = "ridewestside:settings";

type MapProvider = "auto" | "apple" | "google" | "osm";

interface Settings {
  mapProvider: MapProvider;
}

function loadSettings(): Settings {
  try {
    const stored = localStorage.getItem(SETTINGS_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as Partial<Settings>;
      return { mapProvider: parsed.mapProvider || "auto" };
    }
  } catch {
    // ignore
  }
  return { mapProvider: "auto" };
}

function saveSettings(settings: Settings): void {
  try {
    localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // ignore
  }
}

function getMapUrl(location: string): string {
  const { mapProvider } = loadSettings();
  const encoded = encodeURIComponent(location);
  if (mapProvider === "apple") {
    return `https://maps.apple.com/?q=${encoded}`;
  } else if (mapProvider === "google") {
    return `https://www.google.com/maps/search/?api=1&query=${encoded}`;
  } else if (mapProvider === "osm") {
    return `https://www.openstreetmap.org/search?query=${encoded}`;
  }
  // auto: pick best for the platform
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/i.test(ua)) {
    return `https://maps.apple.com/?q=${encoded}`;
  } else if (/Android/i.test(ua)) {
    return `https://www.google.com/maps/search/?api=1&query=${encoded}`;
  }
  return `https://www.openstreetmap.org/search?query=${encoded}`;
}

/**
 * Parse event date string using date-fns
 */
function parseEventDate(dateStr: string): Date | null {
  try {
    // Try ISO format first, then fall back to natural language parsing
    const parsed = parseISO(dateStr);
    if (isNaN(parsed.getTime())) {
      // Fallback to native Date.parse for formats like "January 12, 2026"
      const fallback = Date.parse(dateStr);
      if (isNaN(fallback)) {
        return null;
      }
      return new Date(fallback);
    }
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Load filter state from URL params or localStorage
 */
function loadFilterState(): FilterState {
  // Priority: URL params > localStorage > defaults
  const urlParams = new URLSearchParams(window.location.search);
  const urlStart = urlParams.get("start");
  const urlEnd = urlParams.get("end");

  const urlTag = urlParams.get("tag");

  // If URL has filters, use those
  if (urlStart !== null || urlEnd !== null || urlTag !== null) {
    return {
      start: urlStart || "",
      end: urlEnd || "",
      tag: urlTag || "",
    };
  }

  // Otherwise try localStorage
  try {
    const stored = localStorage.getItem(FILTER_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as Partial<FilterState>;
      return {
        start: parsed.start || "",
        end: parsed.end || "",
        tag: parsed.tag || "",
      };
    }
  } catch (e) {
    console.warn("Failed to load filter state from localStorage:", e);
  }

  // Default empty filters
  return { start: "", end: "", tag: "" };
}

/**
 * Save filter state to localStorage and update URL
 */
function saveFilterState(state: FilterState): void {
  // Save to localStorage
  try {
    localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(state));
  } catch (e) {
    console.warn("Failed to save filter state to localStorage:", e);
  }

  // Update URL without page reload
  const url = new URL(window.location.href);
  if (state.start) {
    url.searchParams.set("start", state.start);
  } else {
    url.searchParams.delete("start");
  }
  if (state.end) {
    url.searchParams.set("end", state.end);
  } else {
    url.searchParams.delete("end");
  }
  if (state.tag) {
    url.searchParams.set("tag", state.tag);
  } else {
    url.searchParams.delete("tag");
  }

  // Update URL without adding to history if nothing changed
  const newUrl = url.toString();
  if (newUrl !== window.location.href) {
    window.history.replaceState({}, "", newUrl);
  }
}

function addMapButton(card: HTMLElement): void {
  const address = card.getAttribute("data-start-address");
  if (!address) return;

  const startName = card.getAttribute("data-start") || address;
  const actions = card.querySelector(".event-actions");
  if (!actions) return;

  const btn = document.createElement("button");
  btn.className = "map-button";
  btn.setAttribute("aria-label", `Navigate to ${startName}`);
  btn.title = `Navigate to ${startName}`;
  btn.innerHTML = `<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
    <path d="M21 3L3 10.53v.98l6.84 2.65L12.48 21h.98L21 3z"/>
  </svg>`;

  btn.addEventListener("click", () => {
    window.open(getMapUrl(address), "_blank", "noopener,noreferrer");
  });

  const shareBtn = actions.querySelector(".share-button");
  if (shareBtn) {
    actions.insertBefore(btn, shareBtn);
  } else {
    actions.appendChild(btn);
  }
}

function initSettings(): void {
  const openBtn = document.getElementById("settings-open");
  const modal = document.getElementById("settings-modal");
  const closeBtn = document.getElementById("settings-close");
  const providerSelect = document.getElementById(
    "settings-map-provider",
  ) as HTMLSelectElement | null;

  if (!openBtn || !modal) return;

  const settings = loadSettings();
  if (providerSelect) {
    providerSelect.value = settings.mapProvider;
  }

  function openModal(): void {
    modal!.removeAttribute("hidden");
    closeBtn?.focus();
  }

  function closeModal(): void {
    modal!.setAttribute("hidden", "");
    openBtn!.focus();
  }

  openBtn.addEventListener("click", openModal);
  closeBtn?.addEventListener("click", closeModal);

  modal.addEventListener("click", (e) => {
    if (e.target === modal) closeModal();
  });

  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && !modal.hasAttribute("hidden")) closeModal();
  });

  providerSelect?.addEventListener("change", () => {
    settings.mapProvider = providerSelect.value as MapProvider;
    saveSettings(settings);
  });
}

/**
 * Add location display to event cards
 */
function addLocationDisplay(card: HTMLElement): void {
  const startLoc = card.getAttribute("data-start");
  const endLoc = card.getAttribute("data-end");

  // Build location text
  let locationText = "";
  if (startLoc && endLoc) {
    // If start and end are the same, only show once
    locationText = startLoc === endLoc ? startLoc : `${startLoc} → ${endLoc}`;
  } else if (startLoc) {
    locationText = startLoc;
  } else if (endLoc) {
    locationText = endLoc;
  }

  // If we have location text, add it to the card
  if (locationText) {
    const eventInfo = card.querySelector(".event-info");
    if (eventInfo) {
      let locationSpan = eventInfo.querySelector(".link-location");
      if (!locationSpan) {
        locationSpan = document.createElement("span");
        locationSpan.className = "link-location";
        eventInfo.appendChild(locationSpan);
      }
      locationSpan.textContent = locationText;
    }
  }
}

function initEventFiltering(): void {
  const eventsSection = document.querySelector('[data-section="events"]');
  const pastSection = document.querySelector('[data-section="past-events"]');
  const futureSection = document.querySelector(
    '[data-section="future-events"]',
  );

  if (!eventsSection) {
    return;
  }

  const eventCards = eventsSection.querySelectorAll<HTMLElement>(
    ".event-card[data-date]",
  );
  const now = startOfDay(new Date());
  const futureThreshold = addDays(now, DAYS_THRESHOLD);

  const pastEvents: EventElement[] = [];
  const upcomingEvents: EventElement[] = [];
  const futureEvents: EventElement[] = [];

  eventCards.forEach((card) => {
    const dateStr = card.getAttribute("data-date");
    if (!dateStr) return;

    const eventDate = parseEventDate(dateStr);
    if (!eventDate) return;

    if (isBefore(eventDate, now)) {
      // Recurring-generated occurrences are only ever built forward from
      // "today" as of the last site build. Once that date has actually
      // passed, drop the card instead of dumping it into Past Rides, so a
      // stretch of un-rebuilt time doesn't clutter the historical view with
      // routine happy hours.
      if (card.hasAttribute("data-recurring")) {
        card.remove();
        return;
      }
      pastEvents.push({ element: card, date: eventDate });
    } else if (isAfter(eventDate, futureThreshold)) {
      futureEvents.push({ element: card, date: eventDate });
    } else {
      upcomingEvents.push({ element: card, date: eventDate });
    }
  });

  // Sort upcoming events by date ascending (soonest first)
  upcomingEvents.sort((a, b) => a.date.getTime() - b.date.getTime());

  // Add location displays and re-append upcoming events in sorted order
  upcomingEvents.forEach(({ element }) => {
    addLocationDisplay(element);
    addMapButton(element);
    eventsSection.appendChild(element);
  });

  // Move past events to past section
  if (pastSection) {
    const pastContainer = pastSection.querySelector(".collapsible-container");
    if (pastContainer && pastEvents.length > 0) {
      // Sort past events by date descending (most recent first)
      pastEvents.sort((a, b) => b.date.getTime() - a.date.getTime());

      pastEvents.forEach(({ element }) => {
        addLocationDisplay(element);
        addMapButton(element);
        pastContainer.appendChild(element);
      });

      pastSection.classList.add("has-events");

      const countEl = pastSection.querySelector(".section-count");
      if (countEl) {
        countEl.textContent = `(${pastEvents.length})`;
      }
    }
  }

  // Move future events to future section
  if (futureSection) {
    const futureContainer = futureSection.querySelector(
      ".collapsible-container",
    );
    if (futureContainer && futureEvents.length > 0) {
      // Sort future events by date ascending (soonest first)
      futureEvents.sort((a, b) => a.date.getTime() - b.date.getTime());

      futureEvents.forEach(({ element }) => {
        addLocationDisplay(element);
        addMapButton(element);
        futureContainer.appendChild(element);
      });

      futureSection.classList.add("has-events");

      const countEl = futureSection.querySelector(".section-count");
      if (countEl) {
        countEl.textContent = `(${futureEvents.length})`;
      }
    }
  }

  // If no upcoming events, show a message
  if (upcomingEvents.length === 0) {
    const noEventsMsg = document.createElement("p");
    noEventsMsg.className = "no-events-message";
    noEventsMsg.textContent = "No upcoming events scheduled. Check back soon!";
    eventsSection.appendChild(noEventsMsg);
  }
}

function initCollapsibleToggles(): void {
  const toggles = document.querySelectorAll<HTMLButtonElement>(
    ".collapsible-toggle",
  );

  toggles.forEach((toggle) => {
    const containerId = toggle.getAttribute("aria-controls");
    if (!containerId) return;

    const container = document.getElementById(containerId);
    if (!container) return;

    toggle.addEventListener("click", () => {
      const isExpanded = toggle.getAttribute("aria-expanded") === "true";
      toggle.setAttribute("aria-expanded", String(!isExpanded));
      container.classList.toggle("expanded");

      const icon = toggle.querySelector(".toggle-icon");
      if (icon) {
        icon.textContent = isExpanded ? "+" : "-";
      }
    });
  });
}

function getCardTags(card: HTMLElement): string[] {
  return (card.getAttribute("data-tags") || "")
    .split(" ")
    .filter(Boolean);
}

function initFilters(): void {
  const startSelect = document.getElementById(
    "filter-start",
  ) as HTMLSelectElement | null;
  const endSelect = document.getElementById(
    "filter-end",
  ) as HTMLSelectElement | null;
  const tagSelect = document.getElementById(
    "filter-tag",
  ) as HTMLSelectElement | null;
  const clearButton = document.getElementById(
    "filter-clear",
  ) as HTMLButtonElement | null;

  if (!startSelect || !endSelect) {
    return;
  }

  const allEventCards = document.querySelectorAll<HTMLElement>(".event-card");

  // Collect unique start locations, end locations, and tags
  const startLocations = new Set<string>();
  const endLocations = new Set<string>();
  const allTags = new Set<string>();

  allEventCards.forEach((card) => {
    const start = card.getAttribute("data-start");
    const end = card.getAttribute("data-end");
    if (start) startLocations.add(start);
    if (end) endLocations.add(end);
    getCardTags(card).forEach((t) => allTags.add(t));
  });

  // Populate start/end dropdowns
  const sortedStarts = Array.from(startLocations).sort();
  const sortedEnds = Array.from(endLocations).sort();

  sortedStarts.forEach((loc) => {
    const option = document.createElement("option");
    option.value = loc;
    option.textContent = loc;
    startSelect.appendChild(option);
  });

  sortedEnds.forEach((loc) => {
    const option = document.createElement("option");
    option.value = loc;
    option.textContent = loc;
    endSelect.appendChild(option);
  });

  // Populate tag dropdown
  if (tagSelect) {
    Array.from(allTags)
      .sort()
      .forEach((tag) => {
        const option = document.createElement("option");
        option.value = tag;
        option.textContent = tag;
        tagSelect.appendChild(option);
      });
  }

  // Load initial filter state from URL or localStorage
  const filterState: FilterState = loadFilterState();

  // Apply initial values to dropdowns
  if (filterState.start && sortedStarts.includes(filterState.start)) {
    startSelect.value = filterState.start;
  }
  if (filterState.end && sortedEnds.includes(filterState.end)) {
    endSelect.value = filterState.end;
  }
  if (filterState.tag && tagSelect && allTags.has(filterState.tag)) {
    tagSelect.value = filterState.tag;
  }

  function updateTagChipActiveState(): void {
    document
      .querySelectorAll<HTMLButtonElement>(".event-tag")
      .forEach((chip) => {
        chip.classList.toggle(
          "active",
          !!filterState.tag &&
            chip.getAttribute("data-tag") === filterState.tag,
        );
      });
  }

  function applyFilters(): void {
    allEventCards.forEach((card) => {
      const cardStart = card.getAttribute("data-start") || "";
      const cardEnd = card.getAttribute("data-end") || "";
      const cardTags = getCardTags(card);

      const matchesStart =
        !filterState.start || cardStart === filterState.start;
      const matchesEnd = !filterState.end || cardEnd === filterState.end;
      const matchesTag =
        !filterState.tag || cardTags.includes(filterState.tag);

      if (matchesStart && matchesEnd && matchesTag) {
        card.classList.remove("filtered-out");
      } else {
        card.classList.add("filtered-out");
      }
    });

    updateTagChipActiveState();

    // Show/hide clear button
    if (clearButton) {
      clearButton.style.display =
        filterState.start || filterState.end || filterState.tag
          ? "block"
          : "none";
    }

    // Save state to localStorage and URL
    saveFilterState(filterState);
  }

  // Apply filters on page load if state was restored
  if (filterState.start || filterState.end || filterState.tag) {
    applyFilters();
  }

  startSelect.addEventListener("change", () => {
    filterState.start = startSelect.value;
    applyFilters();
  });

  endSelect.addEventListener("change", () => {
    filterState.end = endSelect.value;
    applyFilters();
  });

  if (tagSelect) {
    tagSelect.addEventListener("change", () => {
      filterState.tag = tagSelect.value;
      applyFilters();
    });
  }

  // Tag chips: clicking toggles that tag as the active filter
  document
    .querySelectorAll<HTMLButtonElement>(".event-tag")
    .forEach((chip) => {
      chip.addEventListener("click", () => {
        const tag = chip.getAttribute("data-tag") || "";
        filterState.tag = filterState.tag === tag ? "" : tag;
        if (tagSelect) tagSelect.value = filterState.tag;
        applyFilters();
      });
    });

  if (clearButton) {
    clearButton.addEventListener("click", () => {
      filterState.start = "";
      filterState.end = "";
      filterState.tag = "";
      startSelect.value = "";
      endSelect.value = "";
      if (tagSelect) tagSelect.value = "";
      applyFilters();
    });
  }
}

/**
 * Initialize share buttons for event cards
 */
function initShareButtons(): void {
  const shareButtons =
    document.querySelectorAll<HTMLButtonElement>(".share-button");

  shareButtons.forEach((button) => {
    button.addEventListener("click", async () => {
      const title = button.getAttribute("data-event-title") || "Event";
      const date = button.getAttribute("data-event-date") || "";
      const url = button.getAttribute("data-event-url") || "";

      const shareData = {
        title: title,
        text: date,
        url: url,
      };

      const shareText = `${title}${date ? `\n${date}` : ""}\n${url}`;

      // Check if native share is supported and can share this data
      if (
        navigator.share &&
        (!navigator.canShare || navigator.canShare(shareData))
      ) {
        try {
          await navigator.share(shareData);
        } catch (err) {
          // User cancelled or share failed
          if ((err as Error).name !== "AbortError") {
            console.warn("Share failed:", err);
            // Fallback to clipboard
            await copyToClipboard(shareText, button);
          }
        }
      } else {
        // Fallback to clipboard for browsers without native share
        await copyToClipboard(shareText, button);
      }
    });
  });
}

/**
 * Copy text to clipboard and provide visual feedback
 */
async function copyToClipboard(
  text: string,
  button: HTMLButtonElement,
): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);

    // Visual feedback
    button.classList.add("copied");
    const originalIcon = button.innerHTML;

    // Show checkmark icon
    button.innerHTML = `
      <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
      </svg>
    `;

    // Reset after 2 seconds
    setTimeout(() => {
      button.classList.remove("copied");
      button.innerHTML = originalIcon;
    }, 2000);
  } catch (err) {
    console.error("Failed to copy to clipboard:", err);
  }
}

// Initialize when DOM is ready
document.addEventListener("DOMContentLoaded", () => {
  initEventFiltering();
  initCollapsibleToggles();
  initFilters();
  initShareButtons();
  initSettings();
});
